import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { billingAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, CreditCard, Loader2, LogOut, AlertCircle } from 'lucide-react';

/**
 * Paywall landing page — shown after signup or any time the workspace's
 * subscription_status is NOT active/trialing/legacy_active. Lets the user
 * resume Stripe Checkout for the plan that was chosen at signup (read from
 * the workspace doc), or pick another plan (button routes to /#pricing).
 *
 * Intentionally minimal. No content from the dashboard is rendered here so
 * an unpaid account never gets a glimpse of paid features.
 */
export default function BillingRequiredPage() {
  const navigate = useNavigate();
  const { user, workspace, logout } = useAuth();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const t = (fr, en) => (isFr ? fr : en);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const planId = (workspace?.plan || user?.workspace_plan || 'starter').toLowerCase();
  const planLabel = ({ starter: 'Starter (29 CAD/mo)', pro: 'Pro (97 CAD/mo)', enterprise: 'Enterprise (297 CAD/mo)' })[planId] || planId;

  const resumeCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await billingAPI.createCheckout({
        plan_id: planId,
        interval: 'monthly',
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setError(t('Aucune URL de paiement reçue.', 'No checkout URL received.'));
    } catch (e) {
      setError(
        e?.response?.data?.detail
          || e?.message
          || t('Impossible de démarrer le paiement.', 'Failed to start checkout.'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background p-4">
      <div className="w-full max-w-lg" data-testid="billing-required-page">
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {t('Paiement requis', 'Payment required')}
            </CardTitle>
            <CardDescription className="text-base">
              {t(
                'Activez votre espace SpaceLeadX en complétant votre abonnement.',
                'Activate your SpaceLeadX workspace by completing your subscription.',
              )}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('Forfait choisi', 'Selected plan')}</span>
                <span className="font-semibold" data-testid="billing-required-plan">{planLabel}</span>
              </div>
              {user?.email && (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-muted-foreground">{t('Compte', 'Account')}</span>
                  <span className="font-medium">{user.email}</span>
                </div>
              )}
            </div>

            {error && (
              <Alert variant="destructive" data-testid="billing-required-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="button"
              className="w-full"
              size="lg"
              onClick={resumeCheckout}
              disabled={loading}
              data-testid="billing-required-pay-btn"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              {t('Compléter le paiement', 'Complete payment')}
            </Button>

            <p className="text-xs text-center text-muted-foreground pt-2">
              {t(
                "Aucun accès au tableau de bord avant que le paiement Stripe soit confirmé. Annulez à tout moment depuis vos paramètres.",
                'No dashboard access until Stripe payment is confirmed. Cancel anytime from your settings.',
              )}
            </p>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={handleLogout}
              data-testid="billing-required-logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('Se déconnecter', 'Log out')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
